import { type NaiveDate, PartialDate } from "@tento-chrono";
import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { Timescale } from "@tento-lona/sheets";

/**
 * The canonical storage period a rendered column persists its cell under.
 *
 * Column dates are DISPLAY dates — they carry the viewer's `weekStart`
 * preference (`lona-pref-first-day`, which defaults to **sunday**). Storage
 * dates must be canonical server periods, because the wire key (`yw-2026-33`)
 * is re-parsed by `RowData`/`ParsedCell` with the default Monday anchor and
 * loses the display anchor entirely.
 *
 * When the row and the column share a granularity, the storage period IS this
 * column — so derive it from the column's **inclusive last day**, never its
 * first. A Sunday-anchored column *starts* on the last day of a Monday week, so
 * `columnDate.start` names the Monday week that shares only that one Sunday —
 * one column to the left. Any cell keyed to a real date (a template seed, a
 * server-materialized row, a formula result) then paints one column to the
 * RIGHT of the day it belongs to, leaving the current column blank. The
 * inclusive last day always lands inside the Monday week that covers 6 of the
 * column's 7 days, which is the period the column stands for. (`formula.ts`
 * derives a column's date the same way: `column.toRange().end.addDays(-1)`.)
 *
 * When the row is COARSER than the column (a spanned month/year row), the
 * storage period is the one CONTAINING the column, derived from the column's
 * start — the same derivation `Timescale.ownedRowPeriod` uses to pick which
 * column owns the spanned row.
 */
export function storageCellDateForColumn(
  timescaleSetting: Timescale | "custom",
  column: ColumnIndex.Key.Resolved.Default,
): NaiveDate.Partial {
  const [columnDate] = column.discrete();
  if (timescaleSetting === "custom") return columnDate;
  if (timescaleSetting === columnDate.type) {
    return PartialDate.fromDateAndType(
      timescaleSetting,
      columnDate.end.addDays(-1),
    );
  }
  return PartialDate.fromDateAndType(timescaleSetting, columnDate.start);
}
